import { useState, type FormEvent } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  FolderSync,
  Loader2,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import {
  useCreateSource,
  useDeleteSource,
  useSources,
  useSyncSource,
  useUpdateSource,
  type SourceOut,
} from "@/hooks/use-sources";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/utils";

interface SharePointSectionProps {
  projectId: string;
}

export function SharePointSection({ projectId }: SharePointSectionProps) {
  const sourcesQuery = useSources(projectId);
  const createSource = useCreateSource(projectId);
  const syncSource = useSyncSource(projectId);
  const updateSource = useUpdateSource(projectId);
  const deleteSource = useDeleteSource(projectId);

  const [connectOpen, setConnectOpen] = useState(false);
  const [pendingRename, setPendingRename] = useState<SourceOut | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SourceOut | null>(null);

  const sources = sourcesQuery.data ?? [];

  return (
    <section className="space-y-4">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-2xl">
          <h2 className="text-sm font-heading font-semibold text-ink flex items-center gap-2">
            <Cloud className="w-4 h-4 text-content-muted" />
            Fuentes de SharePoint
          </h2>
          <p className="text-xs text-content-muted mt-1">
            Conecta una biblioteca o carpeta de SharePoint. El sistema la
            sincroniza periódicamente: los documentos nuevos o modificados se
            ingieren solos y los borrados se retiran del proyecto.
          </p>
        </div>
        <Button onClick={() => setConnectOpen(true)}>
          <Plus className="w-4 h-4" />
          Conectar carpeta
        </Button>
      </div>

      {/* Lista */}
      {sourcesQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-content-muted animate-spin" />
        </div>
      ) : sourcesQuery.isError ? (
        <div className="bg-danger/10 border border-danger/30 rounded-md p-3 text-center text-sm text-danger">
          {sourcesQuery.error?.message ?? "Error cargando las fuentes."}
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-line-strong rounded-md">
          <FolderSync className="w-8 h-8 text-content-muted/50 mx-auto mb-2" />
          <p className="text-sm text-content font-medium">
            Ninguna carpeta de SharePoint conectada
          </p>
          <p className="text-xs text-content-muted mt-0.5">
            Pulsa «Conectar carpeta» y pega la URL de la biblioteca o carpeta.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((s) => {
            const syncing = syncSource.isPending && syncSource.variables === s.id;
            return (
              <div
                key={s.id}
                className="bg-surface rounded-lg border border-line p-4 flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink truncate" title={s.display_name ?? s.folder_path ?? s.drive_id}>
                    {s.display_name || s.folder_path || "Biblioteca completa"}
                  </p>
                  <p className="text-[11px] text-content-muted mt-0.5 truncate">
                    {s.folder_path ? `Carpeta: ${s.folder_path}` : "Biblioteca completa (raíz)"}
                    {" · "}
                    <span className="text-content">{s.doc_count} documento{s.doc_count === 1 ? "" : "s"}</span>
                  </p>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                    {s.last_sync_error ? (
                      <span className="inline-flex items-center gap-1 text-danger">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Error de sincronización
                      </span>
                    ) : s.last_synced_at ? (
                      <span className="inline-flex items-center gap-1 text-success">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Sincronizado {formatDate(s.last_synced_at)}
                      </span>
                    ) : (
                      <span className="text-content-muted">Sin sincronizar todavía</span>
                    )}
                  </div>
                  {s.last_sync_error && (
                    <p
                      className="mt-1 text-xs text-danger break-words max-w-[60ch]"
                      title={s.last_sync_error}
                    >
                      {s.last_sync_error.length > 200
                        ? `${s.last_sync_error.slice(0, 200)}…`
                        : s.last_sync_error}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => syncSource.mutate(s.id)}
                    disabled={syncing}
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="w-4 h-4" />
                    )}
                    Sincronizar
                  </Button>
                  <button
                    onClick={() => setPendingRename(s)}
                    className="p-2 text-content-muted hover:text-ink rounded transition-colors"
                    title="Renombrar fuente"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPendingDelete(s)}
                    className="p-2 text-content-muted hover:text-danger rounded transition-colors"
                    title="Eliminar fuente"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConnectSharePointDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        pending={createSource.isPending}
        onSubmit={(payload) =>
          createSource.mutate(payload, {
            onSuccess: () => setConnectOpen(false),
          })
        }
      />

      <RenameSourceDialog
        source={pendingRename}
        onClose={() => setPendingRename(null)}
        pending={updateSource.isPending}
        onSubmit={(name) => {
          if (!pendingRename) return;
          updateSource.mutate(
            { id: pendingRename.id, data: { display_name: name } },
            { onSuccess: () => setPendingRename(null) }
          );
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Eliminar fuente"
        description={`Se desconectará «${pendingDelete?.display_name ?? pendingDelete?.folder_path ?? "esta carpeta"}». Los documentos ya ingeridos no se borran; deja de sincronizarse.`}
        confirmLabel="Eliminar"
        variant="destructive"
        loading={deleteSource.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteSource.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Diálogo: conectar por URL (o avanzado: site_id + drive_id)
// ---------------------------------------------------------------------------

interface ConnectDialogProps {
  open: boolean;
  onClose: () => void;
  pending: boolean;
  onSubmit: (payload: {
    sharepoint_url?: string;
    site_id?: string;
    drive_id?: string;
    folder_path?: string;
    display_name?: string;
  }) => void;
}

function ConnectSharePointDialog({
  open,
  onClose,
  pending,
  onSubmit,
}: ConnectDialogProps) {
  const [url, setUrl] = useState("");
  const [displayName, setDisplayName] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!url.trim() || pending) return;
    onSubmit({
      sharepoint_url: url.trim(),
      display_name: displayName.trim() || undefined,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Conectar carpeta de SharePoint"
      description="Pega la URL de la biblioteca o carpeta. El sistema resolverá el sitio y la carpeta automáticamente."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label
            htmlFor="sp-url"
            className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
          >
            URL de SharePoint
          </label>
          <input
            id="sp-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            autoFocus
            className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
            placeholder="https://…sharepoint.com/sites/…/Documentos/Carpeta"
          />
          <p className="text-[11px] text-content-muted">
            Vale el enlace de la carpeta abierta en el navegador. Se sincronizará
            esa carpeta (y sus subcarpetas).
          </p>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="sp-name"
            className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
          >
            Nombre (opcional)
          </label>
          <input
            id="sp-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={300}
            className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
            placeholder="Ej. Informes de seguimiento"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!url.trim() || pending}>
            {pending && <Loader2 className="w-4 h-4 animate-spin" />}
            Conectar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Diálogo: renombrar fuente
// ---------------------------------------------------------------------------

function RenameSourceDialog({
  source,
  onClose,
  pending,
  onSubmit,
}: {
  source: SourceOut | null;
  onClose: () => void;
  pending: boolean;
  onSubmit: (name: string) => void;
}) {
  return (
    <Dialog
      open={source !== null}
      onClose={onClose}
      title="Renombrar fuente"
      description="Cambia el nombre con el que se muestra esta carpeta de SharePoint."
    >
      {source && (
        <RenameForm
          key={source.id}
          initial={source.display_name ?? source.folder_path ?? ""}
          pending={pending}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Dialog>
  );
}

function RenameForm({
  initial,
  pending,
  onClose,
  onSubmit,
}: {
  initial: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(initial);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || pending) return;
    onSubmit(name.trim());
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <label
          htmlFor="sp-rename"
          className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
        >
          Nombre
        </label>
        <input
          id="sp-rename"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          maxLength={300}
          className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
        />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!name.trim() || pending}>
          {pending && <Loader2 className="w-4 h-4 animate-spin" />}
          Guardar
        </Button>
      </div>
    </form>
  );
}
